






































































































import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { State, Getter, Action } from 'vuex-class';
import { IDocumentPage } from '@/types/document-page';
import EventBus from '@/common/event-bus';
import { IField } from '@/fields/types/field';
import Field from '@/fields/components/Field.vue';
import { IFieldPosition } from '@/fields/types/field-position';
import { EAlignFieldType } from '@/fields/context-menu/types/enums/align-field-type';
import { EFieldAction } from '@/fields/types/enums/field-action';
import { EFieldType } from '@/fields/types/enums/field-type';
import { EFieldSource } from '@/fields/types/enums/field-source';
import { IRecipient } from '@/types/Recipient';
import { IUser } from '@/types/User';
import { IFieldPicker } from '@/fields/types/field-picker';
import { IMergeFieldMapObject } from '@/fields/merge-field/types/merge-field-map-object';
import {
  calculateFieldPositionForSave,
  calculateFieldPositionForPlacement,
} from '@/fields/helpers/field-placement.helper';
import DragMultipleFieldsContainer from '@/fields/components/DragMultipleFieldsContainer.vue';
import { EnumDocumentType } from '@/enums/DocumentType';
import PageSkeletonLoader from '@/dealroom/components/PageSkeletonLoader.vue';
import { throttle } from '@/common/helpers';
import { namespace as dealroomNamespace } from '@/dealroom/store/dealroom.store';
import { namespace as fieldNamespace } from '@/fields/store/field.store';
import FieldSelectBox from '@/fields/components/FieldSelectBox.vue';
import { ISelectedPageFieldIds } from '@/fields/types/selected-page-field-ids';
import { actionEndSelectBoxFields } from '@/fields/store/field.actions';
import { getterIsTemplate } from '@/dealroom/store/dealroom.getters';
import { actionShowWarningAlert } from '@/store/actions/alert.actions';
import { namespace as alertNamespace } from '@/store/stores/alert.store';
import { translate } from '@/helpers/translation.helper';
import { FieldHelper } from '@/fields/helpers/field.helper';

@Component({
  components: {
    DragMultipleFieldsContainer,
    Field,
    PageSkeletonLoader,
    FieldSelectBox,
  },
})
export default class DocumentContentPage extends Vue {
  @State('markedPageFieldIds', { namespace: fieldNamespace })
  markedPageFieldIds: ISelectedPageFieldIds;

  @Action(actionEndSelectBoxFields, { namespace: fieldNamespace }) actionEndSelectBoxFields: any;
  @Action(actionShowWarningAlert, { namespace: alertNamespace }) actionShowWarningAlert: any;
  @Getter(getterIsTemplate, { namespace: dealroomNamespace })
  isTemplate: boolean;

  @Prop() page!: IDocumentPage;
  @Prop() sender!: IUser;

  @Prop() selectedFieldIds: string[];
  @Prop() currentPageId: string;
  @Prop() recipients: IRecipient[];
  @Prop() pages: IDocumentPage[];
  @Prop() pageWidth: number;
  @Prop() mergeFieldMapObjects: IMergeFieldMapObject[];
  @Prop() selectedFields: IField[];
  @Prop() documentType: EnumDocumentType;
  @Prop() focusedFieldId: string;
  @Prop() tabFieldId: string;

  fields: IField[] = [];
  fieldsAreLoaded: boolean = true;
  multiSelectMode: boolean = false;
  isDraggingMultipleFields: boolean = false;
  imageIsLoaded: boolean = false;
  imageError: boolean = false;
  newestField: IField;
  isContextMenuOpen: boolean = false;

  throttledTabField = throttle(this.tabField, 200);

  @Watch('pageWidth')
  watchingWidth() {
    this.fields = this.getFields(this.page.fields);
  }

  @Watch('focusedFieldId')
  scrollFieldToView() {
    if (this.focusedFieldId) {
      const focusedField: HTMLElement = document.getElementById(this.focusedFieldId);
      if (focusedField) {
        this.$nextTick(() => focusedField.scrollIntoView({ behavior: 'smooth', block: 'center' }));
      }
    }
  }

  @Watch('page')
  watchingPage(newValue: IDocumentPage, oldValue: IDocumentPage) {
    this.imageError = false;
    this.fields = this.getFields(newValue.fields);

    const isCurrentPage: boolean = !this.currentPageId || this.currentPageId === newValue.id;
    const newValueHasFields: boolean = newValue.fields.length > 0;
    const oldValueHasFields: boolean = oldValue.fields.length > 0;
    const newFieldAdded: boolean =
      isCurrentPage &&
      newValueHasFields &&
      oldValueHasFields &&
      oldValue.fields[oldValue.fields.length - 1].isLoading &&
      !newValue.fields[newValue.fields.length - 1].isLoading;

    if (newFieldAdded) {
      this.newestField = newValue.fields[newValue.fields.length - 1];
      this.handleSelect(this.newestField);
    }
  }

  @Watch('tabFieldId')
  scrollToTabbedField() {
    if (this.tabFieldId) {
      const tabbedField: HTMLElement = document.getElementById(this.tabFieldId);
      if (tabbedField) {
        this.$nextTick(() => tabbedField.scrollIntoView({ behavior: 'smooth', block: 'center' }));
      }
    }
  }

  get showFields(): boolean {
    return this.fieldsAreLoaded && this.imageIsLoaded && !this.imageError;
  }

  get documentPageElementId(): string {
    return `document-content-page-${this.page.id}`;
  }

  get unselectedFields(): IField[] {
    if (this.selectedFieldIds.length < 2) {
      return this.fields;
    }
    return this.fields.filter((field: IField) => !this.selectedFieldIds.includes(field.id));
  }

  get isFieldsPage(): boolean {
    if (this.selectedFields.length === 0) {
      return false;
    }
    return this.selectedFields[0].pageId === this.page.id;
  }

  get skeletonHeight(): number {
    return (this.pageWidth / this.page.pageWidth) * this.page.pageHeight;
  }

  showMenuOnDefault(field: IField): boolean {
    if (!this.newestField || field.id !== this.newestField.id) {
      return false;
    }
    switch (field.type) {
      case EFieldType.Dropdown:
      case EFieldType.Link: {
        return true;
      }
      default:
        return false;
    }
  }

  handleSaveFieldPositions(fields: IField[]) {
    this.$emit('fieldRequest', {
      type: EFieldAction.Update,
      payload: fields.map((field: IField) => ({
        ...field,
        position: calculateFieldPositionForSave(field.position, this.pageWidth),
      })),
    });
  }

  handleDrop(event) {
    if (this.imageError) {
      return;
    }
    const droppedData: string = event.dataTransfer.getData('text');
    if (!droppedData) {
      return;
    }
    try {
      const picker: IFieldPicker = JSON.parse(droppedData);
      const page: ClientRect = document
        .getElementById(this.documentPageElementId)
        .getBoundingClientRect();
      const dropPosition: {
        top: number;
        left: number;
      } = this.getPositionFromDropEvent(event, page, picker);
      const payload = {
        documentPage: this.page,
        type: picker.type,
        mergeValue: picker.mergeValue,
        realWidth: this.pageWidth,
        realHeight: page.height,
        top: dropPosition.top,
        left: dropPosition.left,
      };
      this.$emit('fieldRequest', {
        type: EFieldAction.Create,
        payload,
      });
    } catch (error) {
      this.actionShowWarningAlert(translate('could-not-create-field-try-again'));
    }
  }

  getMergeFieldMapObject(
    field: IField,
    mergeFieldMapObjects: IMergeFieldMapObject[]
  ): IMergeFieldMapObject {
    if (mergeFieldMapObjects.length === 0) {
      return;
    }
    if (field.type === EFieldType.Merge) {
      return mergeFieldMapObjects.find(
        (mergeFieldMapObject: IMergeFieldMapObject) => mergeFieldMapObject.fieldId === field.id
      );
    }
    return;
  }

  getPositionFromDropEvent(
    event,
    page: ClientRect,
    picker: IFieldPicker
  ): { top: number; left: number } {
    const offset: { left: number; top: number } = FieldHelper.getDragImageOffset(
      picker.type,
      this.pageWidth
    );
    return {
      top: event.clientY - page.top - offset.top,
      left: event.clientX - page.left - offset.left,
    };
  }

  getFullName(field: IField, recipients: IRecipient[], sender: IUser): string {
    switch (field.source) {
      case EFieldSource.Unknown:
      case EFieldSource.Document:
        return;
      case EFieldSource.Recipient:
        return this.getRecipientFullName(field.recipientId, recipients);
      case EFieldSource.Sender:
        return !!sender && sender.fullName;
    }
  }

  getRecipientFullName(recipientId: string, recipients: IRecipient[]): string {
    if (!recipients || recipients.length === 0) {
      return '';
    }
    const recipient: IRecipient = recipients.find(
      (recipient: IRecipient) => recipient.id === recipientId
    );
    if (!recipient) {
      return '';
    }
    return recipient.fullName || recipient.roleName;
  }

  fieldInSelectBox(fieldId: string): boolean {
    if (this.page.id !== this.markedPageFieldIds.pageId) {
      return false;
    }
    return this.markedPageFieldIds.fieldIds.includes(fieldId);
  }

  handleLoaded() {
    this.imageIsLoaded = true;
    this.imageError = false;
  }

  handleImgError() {
    this.imageError = true;
  }

  handleDeselectAllFields() {
    if (this.multiSelectMode) {
      return;
    }
    this.$emit('fieldRequest', {
      type: EFieldAction.DeselectAll,
    });
  }

  handleSaveFields(fields: IField[]) {
    const mappedFields: IField[] = fields.map((field: IField) => ({
      ...field,
      position: calculateFieldPositionForSave(field.position, this.pageWidth),
    }));
    this.$emit('fieldRequest', {
      type: EFieldAction.Update,
      payload: mappedFields,
    });
  }

  handleDeleteFields() {
    this.$emit('fieldRequest', {
      type: EFieldAction.Delete,
    });
  }

  handleMoveFields(payload: string) {
    this.$emit('fieldRequest', {
      type: EFieldAction.Move,
      payload,
    });
  }

  handleDuplicateFields() {
    this.$emit('fieldRequest', {
      type: EFieldAction.Duplicate,
    });
  }

  handleAlignFields(actionType: EAlignFieldType) {
    this.$emit('fieldRequest', {
      type: EFieldAction.Align,
      payload: actionType,
    });
  }

  handleSelect(field: IField) {
    this.handleDeselectAllFields();
    this.$emit('fieldRequest', {
      type: EFieldAction.Select,
      payload: field,
    });
  }

  handleDeselect(field: IField) {
    this.$emit('fieldRequest', {
      type: EFieldAction.Deselect,
      payload: field,
    });
  }

  handleTextSize(size: number) {
    this.$emit('fieldRequest', {
      type: EFieldAction.TextSize,
      payload: size,
    });
  }

  getFields(fields: IField[]) {
    return fields.map((field: IField) => ({
      ...field,
      position: this.calculateFieldPosition(field.position),
    }));
  }

  calculateFieldPosition(fieldPosition: IFieldPosition): IFieldPosition {
    return calculateFieldPositionForPlacement(fieldPosition, this.pageWidth);
  }

  setFieldsVisibility(show: boolean) {
    this.fieldsAreLoaded = show;
  }

  preventContextMenu(e: Event) {
    e.preventDefault();
  }

  handleKeyUp(event: KeyboardEvent) {
    if (event.key === 'Meta' || event.key === 'Control') {
      this.multiSelectMode = false;
    }
  }

  tabField() {
    this.$emit('fieldRequest', {
      type: EFieldAction.Tab,
    });
  }

  handleKeyDown(event: KeyboardEvent) {
    if (this.multiSelectMode) {
      this.multiSelectMode = false;
    }
    if (event.key === 'Meta' || event.key === 'Control') {
      this.multiSelectMode = true;
    }
    if (
      event.key === 'Tab' &&
      this.currentPageId === this.page.id &&
      this.selectedFieldIds.length > 0
    ) {
      event.preventDefault();
      this.throttledTabField();
    }
  }

  turnOffMultiselect() {
    this.multiSelectMode = false;
  }

  handleOpenMenu(open: boolean) {
    this.isContextMenuOpen = open;
  }

  handleSelectBoxFields(selectedPageFieldIds: ISelectedPageFieldIds): void {
    this.$emit('selectBoxFields', selectedPageFieldIds);
  }

  mounted() {
    this.fields = this.getFields(this.page.fields);
    EventBus.$on('dimensions-update-started', () => this.setFieldsVisibility(false));
    EventBus.$on('dimensions-update-finished', () => this.setFieldsVisibility(true));
    document.addEventListener('keydown', this.handleKeyDown);
    document.addEventListener('keyup', this.handleKeyUp);
    document.addEventListener('blur', this.turnOffMultiselect);
    document.addEventListener('focus', this.turnOffMultiselect);
  }

  beforeDestroy() {
    EventBus.$off('dimensions-update-started');
    EventBus.$off('dimensions-update-finished');
    document.removeEventListener('keyup', this.handleKeyUp);
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('blur', this.turnOffMultiselect);
    document.removeEventListener('focus', this.turnOffMultiselect);
  }
}
